import React, { cloneElement, createRef } from "react";

import { DialogProps, Dialog as MuiDialog } from "@material-ui/core";

import { trackEvent } from "@ip-utils";

import { Props as ButtonProps } from "../Button";
import { Button } from "../index";
import Actions from "./Actions";
import Content from "./Content";
import Title from "./Title";

type Props = {
  button?: React.ReactElement;
  buttonProps?: ButtonProps;
  size?: DialogProps["maxWidth"];
  title?: string;
  subtitle?: string;
  onAfterShow?: (arg: any) => void;
  onShow?: (arg: any) => void;
  onClose?: (arg: any) => void;
  onAfterClose?: (arg: any) => void;
} & Partial<DialogProps>;

type State = {
  show: boolean;
};

export default class Dialog extends React.Component<Props, State> {
  public static Content = Content;
  public static Actions = Actions;

  ref: React.RefObject<unknown>;

  constructor(props: Props) {
    super(props);

    this.ref = createRef<typeof MuiDialog>();

    this.state = {
      show: false,
    };
  }

  show = () => {
    if (!this.state.show) {
      const { title, subtitle, onAfterShow, onShow } = this.props;

      trackEvent(
        "Dialog",
        `Show '${title}${subtitle ? ` - ${subtitle}` : ""}'`
      );

      this.setState(
        {
          show: true,
        },
        () => {
          if (onShow) {
            onShow(this);
          }
          if (onAfterShow) {
            setTimeout(onAfterShow, 300, this);
          }
        }
      );
    }
  };

  close = () => {
    if (this.state.show) {
      const { title, subtitle, onAfterClose, onClose } = this.props;

      trackEvent(
        "Dialog",
        `Close '${title}${subtitle ? ` - ${subtitle}` : ""}'`
      );

      this.setState(
        {
          show: false,
        },
        () => {
          if (onClose) {
            onClose(this);
          }
          if (onAfterClose) {
            setTimeout(onAfterClose, 300, this);
          }
        }
      );
    }
  };

  scrollTo = (y = 0) => {
    if (this.ref.current) {
      const content = (this.ref.current as HTMLBodyElement).querySelector(
        ".MuiDialog-scrollBody"
      );
      if (content) {
        content.scrollTo(0, y);
      }
    }
  };

  render() {
    const { button, buttonProps, children, size, subtitle, title } = this.props;

    let showButton,
      content = React.Children.toArray(children);
    if (button) {
      showButton = cloneElement(button, {
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          this.show();
          if (button.props.onClick) {
            button.props.onClick(e);
          }
        },
      });
    } else {
      showButton = (
        <Button outlined {...buttonProps} onClick={this.show}>
          {(buttonProps && buttonProps.title) || title}
        </Button>
      );
    }

    return (
      <React.Fragment>
        {showButton}
        <MuiDialog
          maxWidth={size}
          open={this.state.show}
          ref={this.ref}
          scroll="body"
          onClose={this.close}
        >
          <Title onClose={this.close} title={title} subtitle={subtitle} />
          {content}
        </MuiDialog>
      </React.Fragment>
    );
  }
}
